import {
  Component,
  Directive,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { LandingModelService } from 'projects/common/services/landingModel.service';
import {
  EntitiesAutocompleteProfile,
  EntitiesAutocompleteReturnType,
  EntitiesAutocompleteSearchType, Entity, validEntitiesAutocompleteProfiles
} from "../entities-autocomplete/entities-autocomplete.model";
import { InputModel } from "./inputs.model";
import {
  DatePickerDates,
  DatePickerSize,
  DatePickerVerticalPosition,
  DateSelectionType
} from "@shopping-angular-components/datepicker";
import { isPlatformBrowser } from "@angular/common";
import { HtmlUtilsService } from "../../services/html-utils.service";
import { MediaPictureService } from '../../services/media-picture.service';
import { UpaService } from 'projects/common/services/upa.service';

declare var window: any;

@Directive()
export class AbstractInput implements OnInit, OnChanges, OnDestroy {
  @Input() model: InputModel;
  @Input() formData: any;
  @Input() showValidations: boolean;
  @Input() upaData: any;
  @Input() formGroupedValues: any = {};
  @Input() groupError: boolean;
  @Output() groupedInputsUpdateEvent = new EventEmitter();

  public isInvalid: boolean;
  public requiredError: boolean;
  public regexError: boolean;
  public maxError: boolean;
  public minError: boolean;
  public locale: string;

  public currentUrlQueryParams;

  constructor(@Inject(PLATFORM_ID) public platformId: string) { };

  ngOnInit() {
    if (isPlatformBrowser(this.platformId) && window) {
      this.currentUrlQueryParams = new URLSearchParams(window.location.search);
    }
    this.model.valid = !this.hasErrorOfValueRequired() && !this.isMultipleChoiceOptionalWithMinError();
  }

  getUrlParamValue() {
    return this.currentUrlQueryParams?.get(this.model.key);
  }

  ngOnDestroy() {
    delete this.model.valid;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showValidations?.currentValue != changes.showValidations?.previousValue) {
      this.validate();
    }
    if (changes.groupError?.currentValue != changes.groupError?.previousValue) {
      this.groupError = !!this.hasErrorvalidateGroupValues();
      this.isInvalid = this.requiredError || this.regexError || this.minError || this.maxError || this.groupError;
      this.model.valid = !this.isInvalid;
    }
  }

  setFormDataValue(key, value) {
    this.formData[key] = value;
    const groupId = this.model.validations.group_filtered_values?.id;
    if (groupId) {

      if (!this.formGroupedValues[groupId]) {
        this.formGroupedValues[groupId] = { values: {} };
      }
      this.formGroupedValues[groupId].values[key] = value;
      this.validateFormGroupedValues(groupId);
    }
  }

  deleteFormDataValue(key) {
    delete this.formData[this.model.key];
    const groupId = this.model.validations.group_filtered_values?.id;

    if (groupId) {
      if (!this.formGroupedValues[groupId]) {
        return;
      }
      delete this.formGroupedValues[groupId].values[key];
      this.validateFormGroupedValues(groupId);
    }
  }

  validate() {
    this.regexError = false;
    this.maxError = false;
    this.minError = false;
    this.groupError = false;
    this.requiredError = this.hasErrorOfValueRequired() && !this.isRequiredMultipleChoiceWithMinValidation();

    if (!this.requiredError) {
      this.regexError = this.hasErrorvalidateRegex();
    }
    if (!this.requiredError && !this.regexError) {
      this.minError = this.hasToValidateMinError() && this.hasErrorvalidateNumber("min");
    }
    if (!this.requiredError && !this.regexError && !this.minError) {
      this.maxError = this.hasErrorvalidateNumber("max");
    }
    if (!this.requiredError && !this.regexError && !this.minError && !this.maxError) {
      this.groupError = !!this.hasErrorvalidateGroupValues();
      this.groupedInputsUpdateEvent.emit();
    }

    this.isInvalid = this.requiredError || this.regexError || this.minError || this.maxError || this.groupError;
    this.model.valid = !this.isInvalid;
  }

  hasErrorOfValueRequired() {
    return !this.model.validations.optional && (this.formData[this.model.key] === '' || this.formData[this.model.key] == null);
  }

  isMultipleChoiceOptionalWithMinError() {
    return this.model.type == 'MULTIPLE_CHOICE' && this.model.validations.optional && this.hasErrorvalidateNumber('min');
  }

  hasToValidateMinError() {
    const value = this.formData[this.model.key];
    const isOptional = this.model.validations.optional;
    const isOptionalTextNonEmpty = isOptional && value?.length > 0;
    const isOptionalNumberNotNull = isOptional && this.model.type == 'NUMBER' && value != null;
    return !isOptional || isOptionalTextNonEmpty || isOptionalNumberNotNull;
  }

  hasErrorvalidateRegex() {
    if (this.model.validations.regex_validation == null || this.model.validations.regex_validation.regex == null) {
      return false;
    } else {
      const value = this.formData[this.model.key] || '';
      const match = value.match(this.model.validations.regex_validation.regex);
      return match == null || match[0] != value;
    }
  }

  hasErrorvalidateNumber(maxOrMin: string) {
    if (this.model.validations[maxOrMin] == null) {
      return false;
    } else {
      let value = this.formData[this.model.key] || [];
      if (this.model.type == 'MULTIPLE_CHOICE') {
        value = value.split("|") || [];
      }
      if (this.model.type == 'DATE_RANGE') {
        value = value.split("|");
        value = this.calculateDays(value[0], value[1]);
      }
      const numberValue = (value?.length > -1) ? value.length : value;
      const validationValue = this.model.validations[maxOrMin] || 0;
      return maxOrMin === "max" ? numberValue > validationValue : numberValue < validationValue;
    }
  }

  hasErrorvalidateGroupValues() {
    const groupId = this.model.validations.group_filtered_values?.id;
    if (!groupId?.length) {
      return false;
    } else {
      return this.formGroupedValues[groupId] && !this.formGroupedValues[groupId].isValid;
    }
  }

  validateFormGroupedValues(groupId) {
    const groupValues = Object.values(this.formGroupedValues[groupId].values);
    this.formGroupedValues[groupId].isValid = !groupValues.some((value, index, arr) => arr.indexOf(value) !== index);
    // TODO emit output? to validate other inputs
  }

  isRequiredMultipleChoiceWithMinValidation() {
    return this.model.type == 'MULTIPLE_CHOICE' && !this.model.validations.optional && this.model.validations.min;
  }

  calculateDays(fromDate, toDate) {

    fromDate = new Date(fromDate);
    toDate = new Date(toDate);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      return;
    }
    const millisDiff = toDate - fromDate;
    const daysDiff = millisDiff / (1000 * 60 * 60 * 24);
    return (Math.abs(daysDiff));
  }

}

@Component({
  selector: 'common-input',
  template: `
        <p *ngIf="model.title && !hideTitle" class="input-title -eva-3-bold">{{model.title}}</p>
        <p *ngIf="model.label && labelPosition === 'top'" class="input-label -eva-3-mb-sm">{{model.label}}</p>

        <ng-content></ng-content>

        <p *ngIf="model.label && labelPosition === 'bottom'" class="input-label">{{model.label}}</p>
        <div class="input-errors" *ngIf="showValidations">
            <p *ngIf="requiredError" class="input-error -eva-3-mt-sm">
                <i class="eva-3-icon-warning"></i>
                <em class="input-error-message">{{'forms.errors.required' | translate}}</em>
            </p>

            <p *ngIf="regexError" class="input-error">
                <i class="eva-3-icon-warning"></i>
                <em *ngIf="!model.validations?.regex_validation?.message_error" class="input-error-message">{{'forms.errors.regex' | translate: {'0': model.title?.toLocaleLowerCase()} }}</em>
                <em *ngIf="model.validations && model.validations.regex_validation && model.validations.regex_validation.message_error"
                    class="input-error-message">{{model.validations.regex_validation.message_error}}</em>
            </p>

            <p *ngIf="groupError && model.validations.group_filtered_values?.message_error" class="input-error">
                <i class="eva-3-icon-warning"></i>
                <em class="input-error-message">{{model.validations.group_filtered_values?.message_error}}</em>
            </p>

            <p *ngIf="minError" class="input-error">
                <i class="eva-3-icon-warning"></i>
                <em class="input-error-message">{{'forms.errors.min.' + model.type.toLowerCase() + ( model.validations.min === 1 ?'.singular':'' ) | translate: {'0': model.validations.min} }}</em>
            </p>
        </div>

        <div *ngIf="model.auxiliary_link" (click)="onAuxiliaryLinkClick(model.auxiliary_link, $event)"
            class="auxiliar-link-{{model.auxiliary_link.type}} eva-3-card -eva-3-shadow-static -eva-3-shadow-line">
            <em class="btn-text">{{model.auxiliary_link.text}}</em>
            <i class="eva-3-icon-{{model.auxiliary_link.icon}}"></i>
        </div>
    `,
  styleUrls: ['./inputs.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class CommonInputComponent {
  @Input() model: InputModel;
  @Input() requiredError: boolean;
  @Input() regexError: boolean;
  @Input() maxError: boolean;
  @Input() minError: boolean;
  @Input() groupError: boolean;
  @Input() hideTitle: boolean = false;
  @Input() labelPosition = 'bottom';
  @Input() showValidations: boolean;
  @Input() upaData: any;

  constructor(private upaService: UpaService) { }

  onAuxiliaryLinkClick(linkConfig, event) {
    this.upaService.trackClickTopic({ ...this.upaData, label: linkConfig.name, href: linkConfig.link }, event);
    window.location.href = linkConfig.link;
  }

}

@Component({
  selector: 'text-input',
  template: `
        <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError"  [minError]="minError" [upaData]="upaData">
            <div class="eva-3-input -lg {{type}}" [ngClass]="{'-invalid': isInvalid, '-icon-left': model.icon || currencySymbol}">
                <div class="input-container">
                    <input class="input-tag" type="text" [attr.inputmode]="isNumeric ? 'numeric' : 'text'"
                           [placeholder]="model.placeholder || ''" [attr.maxlength]="model.validations.max" [(ngModel)]="formData[model.key]"
                           (focusout)="onFocusoutHandler()" (focusin)="onFocusinHandler()" (keydown)="validateOnKeyDown && validateOnKeyDown($event)">
                    <i *ngIf="model.icon || currencySymbol" class="input-icon" [ngClass]="model.icon ? ('eva-3-icon-' + model.icon) : ''">{{currencySymbol}}</i>
                </div>
            </div>
        </common-input>
    `,
  encapsulation: ViewEncapsulation.None
})

// TODO faltan validaciones
export class TextInputComponent extends AbstractInput implements OnInit {
  @Input() type: 'TEXT' | 'EMAIL' | 'NUMBER' | 'MONEY' = 'TEXT';

  validateOnKeyDown: Function;
  currencySymbol: string;
  thousandsSymbol: string;
  isNumeric: boolean = false;

  private static REGEX_EMAIL = '[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$';

  constructor(private landingModelService: LandingModelService,
    @Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    if (['NUMBER', 'MONEY'].includes(this.type)) {
      this.isNumeric = true;
      this.validateOnKeyDown = this.isNumber;
    }
    if (this.type == 'MONEY') {
      this.currencySymbol = this.landingModelService.getLandingModel().currency.symbol;
      this.thousandsSymbol = this.landingModelService.getLandingModel().currency.thousandsSymbol;
    }
    const urlParamValue = this.getUrlParamValue();
    if (urlParamValue !== null) {
      this.setFormDataValue(this.model.key, urlParamValue);
    }
  }


  override hasErrorvalidateRegex() {
    const emailError = this.type == 'EMAIL' ? (this.formData[this.model.key] || '').match(TextInputComponent.REGEX_EMAIL) == null : false;
    return emailError || super.hasErrorvalidateRegex();
  }

  isNumber(event) {
    const charCode = event.which || event.keyCode;
    // 0-47 especiales; 48-57 numeros; 96-105 numeros del numpad; 107 +; 109 -
    return charCode && (charCode <= 57 || (charCode >= 96 && charCode <= 105) || charCode == 107 || charCode == 109);
  }

  onFocusoutHandler() {
    if (this.type == 'MONEY' && this.formData[this.model.key]) {
      this.formatMoney();
    }
    super.validate();
  }

  onFocusinHandler() {
    if (this.type == 'MONEY' && this.formData[this.model.key]) {
      const value = this.formData[this.model.key].replace(new RegExp(`\\${this.thousandsSymbol}`, "g"), '');
      this.setFormDataValue(this.model.key, value);
    }
  }

  formatMoney() {
    const value = this.formData[this.model.key].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSymbol);
    this.setFormDataValue(this.model.key, value);
  }
}

@Component({
  selector: 'textarea-input',
  template: `
        <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError" [minError]="minError" [upaData]="upaData">
            <div class="eva-3-textarea -lg" [ngClass]="{'-invalid': isInvalid}">
                <div class="textarea-container">
                    <textarea class="textarea-tag" [placeholder]="model.placeholder || ''" rows="3" [(ngModel)]="formData[model.key]"
                    [attr.maxlength]="model.validations.max" (focusout)="validate()"></textarea>
                </div>
                <!--span class="textarea-message">Quedan ... caracteres</span-->
            </div>
        </common-input>
    `,
  encapsulation: ViewEncapsulation.None
})

// TODO faltan validaciones
export class TextareaInputComponent extends AbstractInput implements OnInit {

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    const urlParamValue = this.getUrlParamValue();
    if (urlParamValue !== null) {
      this.setFormDataValue(this.model.key, urlParamValue);
    }
  }
}

@Component({
  selector: 'selector-input',
  template: `
        <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError" [showValidations]="showValidations" [upaData]="upaData" [labelPosition]="showLabelAtLeft?'none':'bottom'">
            <div class="-eva-3-valign">
              <div class="label-container" *ngIf="showLabelAtLeft">
                <label class="eva-3-h6">{{model.label}}</label>
                <span class="eva-3-caption -eva-3-tc-gray-2">{{model.placeholder}}</span>
              </div>
              <div class="eva-3-select" [ngClass]="model.css_class_names || '-lg'">
                  <div class="select-container">
                      <select class="select-tag" [(ngModel)]="formData[model.key]" (change)="validate()" autocomplete="off" [attr.aria-label]="model.key">
                          <option *ngIf="formData[model.key] === null " selected disabled hidden value="">{{model.placeholder}}</option>
                          <option *ngFor="let option of model.options" class="select-option" [value]="option.value"
                                  [selected]="formData[model.key] === option.value">{{option.name}}</option>
                      </select>
                  </div>
                  <p *ngIf="selectedOption?.dynamic_text" class="eva-3-caption -eva-3-tc-gray-2 ">{{selectedOption.dynamic_text}}</p>
              </div>
              <ng-container *ngFor="let option of model.options">
                <div class="options-inputs-container" *ngIf="option.inputs && formData[model.key] === (option.value || option.name)">
                  <inputs-wrapper [model]="option.inputs" [formData]="formData" [showValidations]="showValidations" ></inputs-wrapper>
                </div>
              </ng-container>
            </div>
        </common-input>
    `,
  encapsulation: ViewEncapsulation.None
})

export class SelectorInputComponent extends AbstractInput implements OnInit {

  showLabelAtLeft: boolean = false;
  selectedOption: any;

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.showLabelAtLeft = !!(this.model.label && this.model.placeholder);
    const defaultOption = this.model.options?.find(_ => _.default_option)?.value;
    if (defaultOption !== undefined && defaultOption !== null) {
      this.setFormDataValue(this.model.key, defaultOption);
      this.selectedOption = this.model.options.find(option => option.value === defaultOption);
    }
    this.validate();
  }

  override validate() {
    super.validate();
    this.selectedOption = this.model.options.find(option => option.value === this.formData[this.model.key]);
  }
}

@Component({
  selector: 'multiple-choice-input',
  template: `
      <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [minError]="minError" [maxError]="maxError" [labelPosition]="'top'" [upaData]="upaData">

          <div class="multiple-choice-options-container">
            <div *ngFor="let option of model.options; let i = index; last as isLast"
              [ngClass]="{'item-half': !isLast,
                          'item-full' : isLast,
                          'eva-3-card -eva-3-shadow-line' : model.options.length > 1,
                          'checked': iconsMode && isSelected(option) }">

              <span class="eva-3-checkbox">
                  <label class="checkbox-label  -eva-3-p-md">
                      <span>
                        <input class="checkbox-tag" type="checkbox" [name]="option.value || ''" [disabled]="!!option.disabled"
                        (change)="updateValues(option.value || '')" [(ngModel)]="formData[option.value || '']">
                        <i *ngIf="!iconsMode" class="eva-3-icon-checkmark checkbox-check -eva-3-mr-md"></i>
                        <i *ngIf="iconsMode" class="eva-3-icon-{{option.icon}} -eva-3-icon-sm"></i>
                      </span>
                      <em [innerHTML]="option.name"></em>
                  </label>
              </span>

            </div>
          </div>

          <ng-container *ngFor="let option of model.options">
                <div class="options-inputs-container" *ngIf="option.inputs && formData[model.key] === (option.value || option.name)">
                    <inputs-wrapper [model]="option.inputs" [formData]="formData" [showValidations]="showValidations"></inputs-wrapper>
                </div>
          </ng-container>

      </common-input>
  `
})

export class MultipleChoiceInputComponent extends AbstractInput implements OnInit {
  iconsMode = false;

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    this.iconsMode = this.model.options.every(opt => !!opt.icon);
    super.ngOnInit();
  }

  updateDisabledInputs() {
    const currentSelectedOptions = this.formData?.[this.model.key]?.split("|") || [];
    const isMaxSelected = currentSelectedOptions.length === this.model.validations.max;
    this.model.options.map(option => {
      const isSelected = this.formData[this.model.key].includes(option.value);
      option.disabled = isMaxSelected && !isSelected;
    });
  }

  isSelected(option) {
    return this.formData[this.model.key]?.includes(option.value);
  }

  updateValues(optionKey: string) {

    let currentSelectedOptions = this.formData?.[this.model.key]?.split("|") || [];
    if (this.model.options?.length) {
      if (this.formData[optionKey]) {
        currentSelectedOptions.push(optionKey);
      }
      else {
        let index = currentSelectedOptions.indexOf(optionKey);
        currentSelectedOptions.splice(index, 1);
      }
      this.formData[this.model.key] = currentSelectedOptions.join("|");
      delete this.formData[optionKey];
    }

    this.validate();

    if (this.model.validations.max) {
      this.updateDisabledInputs();
    }
  }
}

@Component({
  selector: 'radios-input',
  template: `
        <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [upaData]="upaData">
            <div class="radio-group-container{{ cardsMode?'-cards':boxMode?'-boxes':''}}">
                <span *ngFor="let option of model.options; last as isLast" class="eva-3-radio"
                  [ngClass]="{'item-half': cardsMode && !isLast,
                          'item-full' : cardsMode && isLast,
                          'eva-3-card -eva-3-shadow-static-hover' : cardsMode,
                          'checked': cardsMode && isSelected(option) }"
                  [ngStyle]="{'background-image': 'url(' + mediaPictureService.getMediaUrl(option.background_image_key, imgSize) + ')'}">

                    <label class="radio-label-container" [ngClass]="{'-eva-3-p-md': cardsMode}" >
                        <input class="radio-tag" type="radio" [name]="model.key" [value]="option.value || option.name" (change)="updateValues()" [(ngModel)]="formData[model.key]" autocomplete="off">
                        <i *ngIf="!cardsMode" class="radio-circle"></i>
                        <i *ngIf="cardsMode" class="eva-3-icon-{{option.icon}} -eva-3-icon-sm"></i>
                        <em>{{option.name}}</em>
                    </label>
                </span>
            </div>

            <ng-container *ngFor="let option of model.options">
                <div class="options-inputs-container" *ngIf="option.inputs && formData[model.key] === (option.value || option.name)" [ngClass]="{'-eva-3-hide': isHiddenInput(option.inputs)}">
                    <inputs-wrapper [model]="option.inputs" [formData]="formData" [showValidations]="showValidations"></inputs-wrapper>
                </div>
            </ng-container>
        </common-input>
    `
})

export class RadiosInputComponent extends AbstractInput implements OnInit {
  @Input() inputStyle: 'RADIO' | 'CARDS' | 'BOX' = 'RADIO';
  cardsMode = false;
  boxMode = false;
  imgSize = '320x320';

  constructor(public mediaPictureService: MediaPictureService,
    @Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.cardsMode = this.inputStyle === 'CARDS';
    this.boxMode = this.inputStyle === 'BOX';
    const defaultOption = this.model.options?.find(_ => _.default_option)?.value;
    if (defaultOption) {
      this.formData[this.model.key] = defaultOption;
      this.updateValues();
    }
  }

  updateValues() {
    this.validate();
    const currentOption = this.formData[this.model.key];
    this.model.options?.filter(option => (option.value || option.name) != currentOption)
      .forEach(otherOption => {
        if (otherOption.inputs) {
          otherOption.inputs.forEach(inputSection => inputSection.forEach(input => delete this.formData[input.key]));
        }
      })
  }

  isSelected(option) {
    return this.formData[this.model.key] === option.value;
  }

  isHiddenInput(inputs) {
    if (inputs?.[0].length) {
      return inputs.every(inputsGroup => inputsGroup.every((input) => input.type === 'HIDDEN'));
    };
  }
}

@Component({
  selector: 'checkbox-input',
  template: `
      <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError" [upaData]="upaData">
          <div class="checkbox-group-container">
              <span *ngFor="let option of model.options" class="eva-3-checkbox -lg">
                  <label class="checkbox-label">
                      <input class="checkbox-tag" type="checkbox" [name]="model.key" [value]="(option.value || option.name).toString()" (change)="updateValues()" [(ngModel)]="formData[model.key]">
                      <i class="eva-3-icon-checkmark  checkbox-check -eva-3-mr-md"></i>
                      <em>{{option.name}}</em>
                  </label>
              </span>
          </div>

          <ng-container *ngFor="let option of model.options">
              <div class="options-inputs-container" *ngIf="option.inputs && formData[model.key]?.toString() === (option.value || option.name)">
                  <inputs-wrapper [model]="option.inputs" [formData]="formData" [showValidations]="showValidations"></inputs-wrapper>
              </div>
          </ng-container>
      </common-input>
  `
})

export class CheckboxInputComponent extends AbstractInput implements OnInit {

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    const urlParamValue = this.getUrlParamValue();
    if (urlParamValue !== null) {
      this.formData[this.model.key] = urlParamValue;
    } else {
      const defaultOption = this.model.options?.find(_ => _.default_option)?.value;
      if (defaultOption) {
        this.formData[this.model.key] = defaultOption;
        this.updateValues();
      }
    }
  }

  updateValues() {
    this.validate();
    const currentOption = this.formData[this.model.key];
    this.model.options?.filter(option => (option.value || option.name) != currentOption)
      .forEach(otherOption => {
        if (otherOption.inputs) {
          otherOption.inputs.forEach(inputSection => inputSection.forEach(input => delete this.formData[input.key]));
        }
      })
  }
}


@Component({
  selector: 'autocomplete-input',
  template: `
    <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError" [groupError]="groupError" [upaData]="upaData" [showValidations]="showValidations">
      <entities-autocomplete [placeholder]="model.placeholder || ''"
                             [locale]="locale"
                             [search]="searchTypes"
                             [profile]="profile"
                             [invalid]="showValidations && isInvalid"
                             [model]="this" propertyName="selectedEntityId"
                             [returnType]="returnType"
                             [icon]="model.icon"
                             [selected]="model['selected']"
                             [mobileTitle]="model.title"
                             (selectedChange)="saveSelectedEntity($event)"
                             (isSelectedChange)="onInputChange($event)">
      </entities-autocomplete>
    </common-input>
  `
})

export class AutocompleteInputComponent extends AbstractInput implements OnInit {
  @Input() product: string;
  @Input() searchTypes: EntitiesAutocompleteSearchType[];

  selectedEntityId: string;
  profile: EntitiesAutocompleteProfile = 'packages';
  returnType: EntitiesAutocompleteReturnType = 'GID|CODE';

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();
    if (validEntitiesAutocompleteProfiles.includes(this.product)) {
      this.profile = this.product
    }

    if (isPlatformBrowser(this.platformId) && window) {
      this.locale = window.locale;
    }

    if (this.model.return_type) {
      this.returnType = this.model.return_type as EntitiesAutocompleteReturnType;
    }

    const urlParamValue = this.getUrlParamValue();
    if (urlParamValue !== null) {
      this.selectedEntityId = urlParamValue;
      this.setFormDataValue(this.model.key, this.selectedEntityId);
    }
    this.validate();
  }

  onInputChange = (isValid: boolean) => {
    if (isValid) {
      this.setFormDataValue(this.model.key, this.selectedEntityId);
    } else {
      this.deleteFormDataValue(this.model.key);
    }
    this.validate();
  }

  saveSelectedEntity = (entity: Entity): void => {
    this.model['selected'] = entity;
  }
}

@Component({
  selector: 'datepicker-input',
  template: `
        <common-input [model]="model" [requiredError]="requiredError" [regexError]="regexError" [groupError]="groupError" [hideTitle]="true" [upaData]="upaData">
          <datepicker product="nymeria-gui" flow="landing"
                      iconPosition="-icon-left"
                      [locale]="datepickerLocale"
                      [selectedDates]="selectedDates"
                      [size]="size"
                      [placeholder]="{from: model.placeholder || '', to: model.placeholder || ''}"
                      [isSingleDatepicker]="!isRangeDates"
                      [dateSelectionType]="dateSelectionType"
                      [verticalPosition]="verticalPosition"
                      [maxDaysRange]="maxDaysRange"
                      [pageviewId]="pageviewId"
                      [invalid]="isInvalid"
                      [fromTitle]="fromTitle"
                      [toTitle]="toTitle"
                      (selected)="selectDates($event)">
          </datepicker>
        </common-input>
    `,
  encapsulation: ViewEncapsulation.None
})

export class DatepickerInputComponent extends AbstractInput implements OnInit {
  @Input() isRangeDates: boolean;

  verticalPosition = DatePickerVerticalPosition.TOP;
  size: DatePickerSize = DatePickerSize.LARGE
  dateSelectionType: DateSelectionType;
  datepickerLocale: string = 'es';
  pageviewId: string;
  selectedDates: DatePickerDates = { startDate: '' };
  fromTitle?: string;
  toTitle?: string;
  maxDaysRange: number;

  constructor(@Inject(PLATFORM_ID) platformId: string,
    private htmlUtilsService: HtmlUtilsService) {
    super(platformId);
  }

  override ngOnInit() {
    this.pageviewId = globalThis?.trackingInfo?.pageviewId || '';
    super.ngOnInit();
    if (isPlatformBrowser(this.platformId) && window) {
      if (!window['preact']) {
        this.htmlUtilsService.insertScript('preact-inputs', 'https://cdnjs.cloudflare.com/ajax/libs/preact/10.1.0/preact.umd.min.js')
      }
      this.locale = window.locale;
    }

    this.dateSelectionType = this.isRangeDates ? DateSelectionType.rangeDates : DateSelectionType.singleDate;
    this.datepickerLocale = this.locale?.replace('_', '-');
    [this.fromTitle, this.toTitle] = this.model.title?.split('|') || [];
    this.selectedDates = this.getSelectedDates();
    this.maxDaysRange = this.model.validations.max || 30;

    const urlParamValue = this.getUrlParamValue();
    if (urlParamValue !== null) {
      this.selectDates(urlParamValue);
    }
  }

  selectDates = (event: DatePickerDates | string) => {
    const value = (typeof event == "string") ? event : (event?.startDate + '|' + event?.endDate);
    this.setFormDataValue(this.model.key, value);
    this.validate()
  }

  getSelectedDates = () => {
    const [startDate, endDate] = this.formData?.[this.model.key]?.split('|') || [];
    return {
      startDate: startDate || '',
      endDate: endDate || ''
    }
  }
}

@Component({
  selector: 'steppers-input',
  template: `
      <common-input [model]="model" [requiredError]="requiredError" [minError]="minError" [maxError]="maxError" [labelPosition]="showLabelAtLeft?'none':'bottom'" [upaData]="upaData">

        <div class="-eva-3-valign">
            <div class="label-container" *ngIf="showLabelAtLeft">
              <label class="eva-3-h6">{{model.label}}</label>
              <span class="eva-3-caption -eva-3-tc-gray-2">{{model.placeholder}}</span>
            </div>

            <div class="input-container eva-3-input  eva-3-steppers" [ngClass]="model.css_class_names || '-md'">
                <input class="steppers-tag" readonly="" tabindex="-1"
                  [attr.max]="model.validations.max" [attr.min]="model.validations.min" [(ngModel)]="formData[model.key]" />
                <button  aria-label="Boton Menos" class="steppers-icon-left eva-3-icon-minus" tabindex="" name=""
                  [disabled]="isDisabled('minus')" (click)="add(-1)"></button>
                <button  aria-label="Boton Mas" class="steppers-icon-right eva-3-icon-plus" tabindex="" name=""
                  [disabled]="isDisabled('plus')" (click)="add(1)"></button>
            </div>
        </div>
      </common-input>
  `,
  encapsulation: ViewEncapsulation.None
})

export class SteppersInputComponent extends AbstractInput implements OnInit {

  public showLabelAtLeft: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.showLabelAtLeft = !!(this.model.label && this.model.placeholder);
    if (isPlatformBrowser(this.platformId)) {
      const urlParamValue = this.getUrlParamValue();
      if (urlParamValue !== null && this.isValidValue(urlParamValue)) {
        this.formData[this.model.key] = parseInt(urlParamValue || "");
      } else {
        this.formData[this.model.key] = parseInt(this.model.default_value || "") || this.model.validations?.min || 0;
      }
      this.validate();
    }
  }

  isValidValue(value) {
    if ((typeof this.model.validations?.min != 'undefined') && !isNaN(this.model.validations.min) && (value < this.model.validations.min)) {
      return false;
    }
    if ((typeof this.model.validations?.max != 'undefined') && !isNaN(this.model.validations.max) && (value > this.model.validations.max)) {
      return false;
    }
    return true;
  }

  add(value) {
    this.formData[this.model.key] += value;
    this.validate();
  }

  isDisabled(stepper) {
    if (stepper == "minus" && (typeof this.model.validations?.min != 'undefined') && !isNaN(this.model.validations.min)) {
      return this.formData[this.model.key] <= this.model.validations?.min;
    }
    if (stepper == "plus" && (typeof this.model.validations?.max != 'undefined') && !isNaN(this.model.validations.max)) {
      return this.formData[this.model.key] >= this.model.validations?.max;
    }
  }
}

@Component({
  selector: 'hidden-input',
  template: `
      <common-input [model]="model" class="-eva-3-hide">
          <input type="hidden" [(ngModel)]="formData[model.key]"/>
      </common-input>
  `,
  encapsulation: ViewEncapsulation.None
})

export class HiddenInputComponent extends AbstractInput implements OnInit {
  constructor(@Inject(PLATFORM_ID) platformId: string) {
    super(platformId);
  }
  override ngOnInit() {
    super.ngOnInit();

    const urlParamValue = this.getUrlParamValue();
    this.setFormDataValue(this.model.key, (urlParamValue !== null) ? urlParamValue : this.model.default_value);

    this.validate();
  }
}

